/* Native PDP quote form. Hydrates from the inline metafield JSON emitted by
   sections/quote-form.liquid, computes the live "Estimated Cost" using the
   formula extracted from lapel-pins-quote-app's pinquote-check.js, and submits
   directly to Magic Make at https://forgemachine.io/storefronts/<id>/quote_requests.

   See docs/plans/2026-05-01-001-feat-pdp-quote-form-magicmake-plan.md.
*/
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var ALLOWED_EXT = ['ai', 'eps', 'jpg', 'jpeg', 'pdf', 'psd', 'tif', 'tiff', 'png', 'bmp'];
  var MAX_FILE_BYTES = 10 * 1024 * 1024;
  var MAX_FILES = 5;

  // Fail-open safety net: if Turnstile never resolves (silent hang, CF
  // outage, or a config error that doesn't trigger the error callback — e.g.
  // the removed size="invisible" value), enable the submit button anyway
  // after this delay so a Turnstile problem can never zero out quote
  // submissions. The token isn't sent to / verified by the backend, so this
  // doesn't weaken any real defense. A genuine pass still enables instantly.
  var TURNSTILE_FALLBACK_MS = 10000;

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function formatAsDollars(n) {
    var v = Number(n);
    if (!isFinite(v)) v = 0;
    return '$' + v.toFixed(2);
  }

  function slug(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Collapse a label to a comparison key: lowercase, drop parentheticals
  // like "(standard)" / "(+$0.40)", then strip everything non-alphanumeric.
  // Lets merchant-friendly wording ("Polybag") match catalog labels
  // ("Poly Bag (standard)").
  function normalizeLabel(s) {
    return String(s == null ? '' : s)
      .toLowerCase()
      .replace(/\([^)]*\)/g, '')
      .replace(/[^a-z0-9]+/g, '');
  }

  // Default option selections applied on page load, keyed by normalized
  // attribute label -> normalized desired option label. Only applied when
  // both the attribute and a matching option exist on the rendered form.
  var DEFAULT_SELECTIONS = {
    platingchoice: 'chooseforme',
    backattachment: 'butterflyclutch',
    packaging: 'polybag',
  };

  // Find the option matching the requested default for an attribute and
  // select it, clearing any prior selection (incl. the placeholder).
  // No-op when the attribute isn't in DEFAULT_SELECTIONS or no option matches.
  function applyDefaultSelection(attr, select) {
    var want = DEFAULT_SELECTIONS[normalizeLabel(attr.attribute_label)];
    if (!want) return;
    var realOptions = Array.prototype.slice.call(select.options).filter(function (o) {
      return o.value !== '';
    });
    var match = null;
    // exact normalized match first, then prefix, then substring
    for (var pass = 0; pass < 3 && !match; pass++) {
      for (var i = 0; i < realOptions.length; i++) {
        var key = normalizeLabel(realOptions[i].dataset.label || realOptions[i].textContent);
        if (!key) continue;
        if ((pass === 0 && key === want) ||
            (pass === 1 && key.indexOf(want) === 0) ||
            (pass === 2 && key.indexOf(want) !== -1)) {
          match = realOptions[i];
          break;
        }
      }
    }
    if (!match) return;
    Array.prototype.forEach.call(select.options, function (o) { o.selected = false; });
    match.selected = true;
  }

  function fileTypeFamily(name) {
    var ext = String(name || '').split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff', 'gif'].indexOf(ext) !== -1) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
  }

  function pickImage(img) {
    if (!img || Array.isArray(img)) return null;
    return img.preview_url || img.img_src || null;
  }

  function attributeHasImages(attr) {
    return (attr.attribute_options || []).some(function (opt) {
      return pickImage(opt.detailed_image) || pickImage(opt.dropdown_image);
    });
  }

  function formatPhone(value) {
    var digits = String(value || '').replace(/\D/g, '').slice(0, 10);
    if (!digits) return '';
    if (digits.length < 4) return '(' + digits;
    if (digits.length < 7) return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
    return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
  }

  function parseConfig(root) {
    var tag = root.querySelector('script[data-quote-config]');
    if (!tag) return null;
    try {
      return JSON.parse(tag.textContent || '{}');
    } catch (err) {
      if (window.console) console.warn('[quote-form] invalid config JSON', err);
      return null;
    }
  }

  // -- override resolution --------------------------------------------------

  // Attribute objects carry per-product `overrides_json` that can override an
  // option's price or hide it entirely. Returns a new attribute with options
  // filtered/mutated accordingly.
  function applyOverrides(attr) {
    var overrides = attr.overrides_json && typeof attr.overrides_json === 'object' && !Array.isArray(attr.overrides_json)
      ? attr.overrides_json
      : {};
    var options = (attr.attribute_options || []).map(function (opt) {
      var ov = overrides[String(opt.id)];
      if (!ov) return opt;
      if (ov.is_hidden) return null;
      var newOpt = Object.assign({}, opt);
      if (typeof ov.price === 'number') {
        newOpt.price = ov.price;
        var sign = ov.price >= 0 ? '+' : '-';
        var amount = Math.abs(ov.price).toFixed(2);
        newOpt.display_label = opt.label + ' (' + sign + '$' + amount + ')';
      }
      return newOpt;
    }).filter(Boolean);
    return Object.assign({}, attr, { attribute_options: options });
  }

  // -- field rendering ------------------------------------------------------

  function makeFieldEl(label, opts) {
    opts = opts || {};
    var wrap = document.createElement('div');
    wrap.className = 'quote-form__field';
    if (opts.dataAttr) wrap.setAttribute(opts.dataAttr, '');
    if (opts.attributeId != null) wrap.setAttribute('data-attribute-id', String(opts.attributeId));
    if (label) {
      var labelEl = document.createElement('label');
      labelEl.className = 'quote-form__label' + (opts.required ? ' quote-form__label--required' : '');
      labelEl.textContent = label;
      if (opts.htmlFor) labelEl.setAttribute('for', opts.htmlFor);
      wrap.appendChild(labelEl);
    }
    if (opts.note) {
      var note = document.createElement('p');
      note.className = 'quote-form__field-note';
      note.textContent = opts.note;
      wrap.appendChild(note);
    }
    return wrap;
  }

  function renderSelectableAttribute(ctx, attr) {
    var fieldId = 'quote-form-attr-' + ctx.sectionId + '-' + attr.attribute_id;
    var field = makeFieldEl(attr.attribute_label, {
      htmlFor: fieldId,
      required: attr.attribute_is_required,
      note: attr.attribute_frontend_note,
      attributeId: attr.attribute_id,
    });

    if (attributeHasImages(attr)) {
      var labelEl = field.querySelector('.quote-form__label');
      if (labelEl) {
        var help = document.createElement('button');
        help.type = 'button';
        help.className = 'quote-form__help-button';
        help.setAttribute('aria-label', 'Show ' + attr.attribute_label + ' options');
        help.textContent = '?';
        // Help button lives inside a <label for="<select-id>">. Without these
        // calls, clicking the button also activates the for-target select.
        help.addEventListener('click', function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          openModal(buildOptionsModalContent(attr));
        });
        labelEl.appendChild(help);
      }
    }

    var select = document.createElement('select');
    select.id = fieldId;
    select.name = slug(attr.attribute_label);
    select.dataset.attributeId = String(attr.attribute_id);
    select.dataset.fieldKind = 'selectable';
    select.dataset.perOrder = attr.attribute_is_price_per_order ? '1' : '0';

    var options = attr.attribute_options || [];
    // If there's only one option there's nothing to choose — skip the
    // "Please select..." placeholder entirely and pre-select it.
    var singleOption = options.length === 1;

    var placeholder = null;
    if (!singleOption) {
      placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Please select...';
      placeholder.disabled = attr.attribute_is_required ? true : false;
      placeholder.selected = true;
      select.appendChild(placeholder);
    }

    options.forEach(function (opt) {
      var o = document.createElement('option');
      o.value = String(opt.id != null ? opt.id : opt.code || opt.label);
      o.textContent = opt.display_label || opt.label;
      o.dataset.price = String(Number(opt.price) || 0);
      o.dataset.label = opt.label;
      if (singleOption || opt.is_default) {
        o.selected = true;
        if (placeholder) placeholder.selected = false;
      }
      select.appendChild(o);
    });

    // Merchant-requested defaults override the placeholder/is_default state
    // when this attribute is configured for one (plating, back, packaging).
    applyDefaultSelection(attr, select);

    field.appendChild(select);
    return field;
  }

  function renderCheckboxAttribute(ctx, attr) {
    var fieldId = 'quote-form-attr-' + ctx.sectionId + '-' + attr.attribute_id;
    var field = makeFieldEl(null, {
      attributeId: attr.attribute_id,
    });
    var labelEl = document.createElement('label');
    labelEl.className = 'quote-form__label quote-form__checkbox';
    labelEl.setAttribute('for', fieldId);

    var input = document.createElement('input');
    input.type = 'checkbox';
    input.id = fieldId;
    input.name = slug(attr.attribute_label);
    input.dataset.attributeId = String(attr.attribute_id);
    input.dataset.fieldKind = 'checkbox';
    input.dataset.perOrder = attr.attribute_is_price_per_order ? '1' : '0';
    var price = Number(
      attr.attribute_checkbox_yes_price != null
        ? attr.attribute_checkbox_yes_price
        : (attr.attribute_options && attr.attribute_options[0] && attr.attribute_options[0].price) || 0,
    );
    input.dataset.price = String(price);
    var optLabel = (attr.attribute_options && attr.attribute_options[0] && attr.attribute_options[0].label) || 'Yes';
    input.dataset.label = optLabel;

    var span = document.createElement('span');
    var prettyLabel = attr.attribute_label;
    if (price > 0) {
      var per = attr.attribute_is_price_per_order ? '' : 'ea';
      prettyLabel += ' (+$' + price.toFixed(2) + (per ? per : '') + ')';
    }
    span.textContent = prettyLabel;

    labelEl.appendChild(input);
    labelEl.appendChild(span);

    if (attributeHasImages(attr)) {
      var help = document.createElement('button');
      help.type = 'button';
      help.className = 'quote-form__help-button';
      help.setAttribute('aria-label', 'Show ' + attr.attribute_label + ' details');
      help.textContent = '?';
      help.addEventListener('click', function (ev) {
        // Don't toggle the checkbox when clicking the help icon.
        ev.preventDefault();
        ev.stopPropagation();
        openModal(buildOptionsModalContent(attr));
      });
      labelEl.appendChild(help);
    }

    field.appendChild(labelEl);

    if (attr.attribute_frontend_note) {
      var note = document.createElement('p');
      note.className = 'quote-form__field-note';
      note.textContent = attr.attribute_frontend_note;
      field.appendChild(note);
    }
    return field;
  }

  function renderAttribute(ctx, attr) {
    if ((attr.attribute_options || []).length === 0) return null;
    if (attr.attribute_input_type === 'checkbox') return renderCheckboxAttribute(ctx, attr);
    return renderSelectableAttribute(ctx, attr);
  }

  // -- primary section (size/qty/date/no_rush) -----------------------------

  function getPricingAttribute(config) {
    var pid = config.product && config.product.pricing_attribute;
    if (!pid) return null;
    return (config.attributes || []).find(function (a) { return a.attribute_id === pid; }) || null;
  }

  // The "primary" attribute is rendered above Quantity. Falls back to the
  // lowest-sort-order attribute when the product has no pricing attribute,
  // so Size (or whatever comes first) anchors the form.
  function getPrimaryAttribute(config) {
    var pricing = getPricingAttribute(config);
    if (pricing) return pricing;
    var sorted = (config.attributes || []).slice().sort(function (a, b) {
      var ao = a.sort_order != null ? a.sort_order : a.attribute_default_sort_order || 999;
      var bo = b.sort_order != null ? b.sort_order : b.attribute_default_sort_order || 999;
      return ao - bo;
    });
    return sorted[0] || null;
  }

  function renderPrimary(ctx) {
    var primary = $(ctx.root, '[data-quote-primary]');
    primary.innerHTML = '';

    var primaryAttr = getPrimaryAttribute(ctx.config);
    if (primaryAttr) {
      var resolved = applyOverrides(primaryAttr);
      var sizeField = renderAttribute(ctx, resolved);
      if (sizeField) {
        sizeField.setAttribute('data-primary-attribute', '1');
        // For the primary attribute (typically Size), move the field note
        // below the input/select to match the original lapelpins UX.
        var existingNote = sizeField.querySelector('.quote-form__field-note');
        if (existingNote) sizeField.appendChild(existingNote);
        primary.appendChild(sizeField);
        ctx.primaryAttrId = primaryAttr.attribute_id;
      }
    }

    // Quantity
    var qtyId = 'quote-form-qty-' + ctx.sectionId;
    var qtyField = makeFieldEl('Quantity', { htmlFor: qtyId, required: true });
    var qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.inputMode = 'numeric';
    qtyInput.id = qtyId;
    qtyInput.name = 'quantity';
    qtyInput.dataset.quoteQuantity = '';
    qtyInput.required = true;
    qtyField.appendChild(qtyInput);

    // MOQ warning: hidden until the customer enters a qty below the minimum
    // for the active pricing row. Per-product quantity_note from the
    // metafield rides along so merchandiser-set guidance surfaces at the
    // same moment the validation tip kicks in.
    var moqNote = document.createElement('p');
    moqNote.className = 'quote-form__field-note quote-form__field-note--moq';
    moqNote.dataset.quoteMoqNote = '';
    moqNote.hidden = true;
    qtyField.appendChild(moqNote);
    // Up-front MOQ guidance lives inside the input as its placeholder. Before
    // a size (pricing attribute) is chosen there's no active row, so fall back
    // to the smallest quantity break across the whole pricing table so the
    // placeholder is populated on page load.
    ctx.refreshMoqPlaceholder = function () {
      var min = minQtyOfRow(activePricingRow(ctx));
      if (min == null) min = minQtyOfTable(ctx);
      qtyInput.placeholder = min != null ? 'Minimum order: ' + min : '';
    };
    ctx.refreshMoqPlaceholder();

    ctx.refreshMoqNote = function () {
      var row = activePricingRow(ctx);
      var min = minQtyOfRow(row);
      if (min == null) min = minQtyOfTable(ctx);
      var qtyEl = $(ctx.root, '[data-quote-quantity]');
      var raw = qtyEl ? qtyEl.value : '';
      var qty = parseInt(raw, 10);
      // The red note is a warning, not guidance: show it only once the
      // customer has actually entered a quantity below the minimum. Empty or
      // a valid (>= min) quantity shows nothing — the minimum itself is
      // already surfaced in the input placeholder.
      if (min == null || raw === '' || !isFinite(qty) || qty <= 0 || qty >= min) {
        moqNote.hidden = true;
        return;
      }
      var bits = ['Minimum order: ' + min];
      var qSettings = ctx.config.quote_form_settings || {};
      var customNote = qSettings.quantity_note || (ctx.config.product && ctx.config.product.quantity_note);
      if (customNote) bits.push(String(customNote).trim());
      moqNote.textContent = bits.join(' · ');
      moqNote.hidden = false;
    };
    ctx.refreshMoqNote();

    primary.appendChild(qtyField);

    var pricingTable = (ctx.config.pricing_table && ctx.config.pricing_table.pricing_table_json) || {};
    if (Object.keys(pricingTable).length) {
      var pricingLink = document.createElement('button');
      pricingLink.type = 'button';
      pricingLink.className = 'quote-form__pricing-link';
      pricingLink.textContent = 'View pricing chart';
      pricingLink.addEventListener('click', function () {
        openModal(buildPricingModalContent(ctx));
      });
      var primaryLabel = primary.querySelector('[data-primary-attribute] .quote-form__label');
      if (primaryLabel) {
        // Tiny SR-only comma so the label + button don't run together in
        // a single utterance ("Size required View pricing chart").
        var srComma = document.createElement('span');
        srComma.className = 'visually-hidden';
        srComma.textContent = ', ';
        primaryLabel.appendChild(srComma);
        primaryLabel.appendChild(pricingLink);
      } else {
        qtyField.appendChild(pricingLink);
      }
    }

    // Date Needed (with optional No Rush nested inside the same field)
    var dateLabel =
      (ctx.config.quote_form_settings && ctx.config.quote_form_settings.need_it_faster_label) || 'Date Needed';
    var dateId = 'quote-form-date-' + ctx.sectionId;
    var dateField = makeFieldEl(dateLabel, { htmlFor: dateId });
    dateField.classList.add('quote-form__field--date');

    // Production-time hint sits inline next to the Date Needed label
    // in muted text — gives the customer a baseline for picking a date
    // without crowding the Quantity field below.
    var production = ctx.config.product && ctx.config.product.standard_production_time;
    if (production) {
      var dateLblEl = dateField.querySelector('.quote-form__label');
      if (dateLblEl) {
        var prodHint = document.createElement('span');
        prodHint.className = 'quote-form__label-aside';
        prodHint.textContent = 'Arrives in ' + production;
        dateLblEl.appendChild(prodHint);
      }
    }

    var dateInput = document.createElement('input');
    dateInput.type = 'date';
    dateInput.id = dateId;
    dateInput.name = 'date_needed';
    dateInput.dataset.quoteDate = '';
    dateField.appendChild(dateInput);

    // Click anywhere on the date field opens the picker.
    dateField.addEventListener('click', function (ev) {
      if (ev.target.closest('input[type="checkbox"], label[for], .quote-form__field-note')) return;
      if (ev.target === dateInput) return; // native handles its own click
      if (dateInput.disabled) return; // No Rush is on; do nothing
      if (typeof dateInput.showPicker === 'function') {
        try { dateInput.showPicker(); return; } catch (_) {}
      }
      dateInput.focus();
    });

    if (ctx.config.product && ctx.config.product.display_no_rush_option) {
      var noRushLabel =
        (ctx.config.quote_form_settings && ctx.config.quote_form_settings.no_rush_label) || 'No Rush';
      var noRushId = 'quote-form-norush-' + ctx.sectionId;
      var lblEl = document.createElement('label');
      lblEl.className = 'quote-form__label quote-form__checkbox quote-form__no-rush';
      lblEl.setAttribute('for', noRushId);
      var nrInput = document.createElement('input');
      nrInput.type = 'checkbox';
      nrInput.id = noRushId;
      nrInput.name = 'no_rush';
      nrInput.dataset.quoteNoRush = '';
      var nrSpan = document.createElement('span');
      nrSpan.textContent = noRushLabel;
      lblEl.appendChild(nrInput);
      lblEl.appendChild(nrSpan);
      dateField.appendChild(lblEl);

      // Mutex between Date Needed and No Rush:
      // - Pick a date  → disable + grey out the No Rush checkbox (and clear it).
      // - Check No Rush → clear + disable + grey out the date input.
      // - Reverse the active one to bring the other back.
      function syncDateNoRushMutex() {
        var hasDate = !!dateInput.value;
        var noRushOn = nrInput.checked;
        if (noRushOn) {
          dateInput.value = '';
          dateInput.disabled = true;
          dateField.classList.add('quote-form__field--disabled');
        } else {
          dateInput.disabled = false;
          dateField.classList.remove('quote-form__field--disabled');
        }
        if (hasDate && !noRushOn) {
          nrInput.checked = false;
          nrInput.disabled = true;
          lblEl.classList.add('quote-form__checkbox--disabled');
        } else {
          nrInput.disabled = false;
          lblEl.classList.remove('quote-form__checkbox--disabled');
        }
      }
      dateInput.addEventListener('change', syncDateNoRushMutex);
      dateInput.addEventListener('input', syncDateNoRushMutex);
      nrInput.addEventListener('change', syncDateNoRushMutex);
    }

    primary.appendChild(dateField);

    // Set quantity min from the active pricing row
    var firstRow = activePricingRow(ctx);
    if (firstRow) {
      var keys = Object.keys(firstRow).map(function (k) { return parseInt(k, 10); }).sort(function (a, b) { return a - b; });
      var min = keys[0];
      if (isFinite(min)) {
        qtyInput.min = String(min);
        qtyInput.placeholder = 'Minimum order: ' + min;
      }
    }
  }

  function renderAttributes(ctx) {
    var host = $(ctx.root, '[data-quote-attributes]');
    host.innerHTML = '';
    var skipId = ctx.primaryAttrId || (ctx.config.product && ctx.config.product.pricing_attribute) || 0;
    var attrs = (ctx.config.attributes || []).slice();
    attrs.sort(function (a, b) {
      var ao = a.sort_order != null ? a.sort_order : a.attribute_default_sort_order || 0;
      var bo = b.sort_order != null ? b.sort_order : b.attribute_default_sort_order || 0;
      return ao - bo;
    });
    attrs.forEach(function (raw) {
      if (raw.attribute_id === skipId) return; // already rendered in primary
      var attr = applyOverrides(raw);
      var el = renderAttribute(ctx, attr);
      if (el) host.appendChild(el);
    });
  }

  function renderHeading(ctx) {
    var headingEl = $(ctx.root, '[data-quote-heading]');
    if (!headingEl) return;
    if (headingEl.textContent && headingEl.textContent.trim()) return;
    var design = ctx.config.product && ctx.config.product.design_object;
    if (design) headingEl.textContent = 'START DESIGNING YOUR ' + design;
  }

  // -- artwork uploader (Uploadcare) ---------------------------------------

  function setupUploader(ctx) {
    var product = ctx.config.product || {};
    if (!product.allow_file_uploads) return;
    var artworkRegion = $(ctx.root, '[data-quote-artwork]');
    if (!artworkRegion) return;
    artworkRegion.hidden = false;

    var multiple = !!product.allow_multiple_file_uploads;
    var holder = $(ctx.root, '[data-quote-uploader]');
    holder.innerHTML = '';
    var input = document.createElement('input');
    input.type = 'hidden';
    input.setAttribute('role', 'uploadcare-uploader');
    input.setAttribute('data-public-key', ctx.uploadcareKey);
    input.setAttribute('data-images-only', 'false');
    input.setAttribute('data-system-dialog', 'true');
    input.setAttribute('data-clearable', 'true');
    input.setAttribute('data-input-accept-types', '.' + ALLOWED_EXT.join(',.'));
    if (multiple) {
      input.setAttribute('data-multiple', 'true');
      input.setAttribute('data-multiple-max', String(MAX_FILES));
    }
    holder.appendChild(input);

    function resolveGroupFiles(group) {
      // Uploadcare 2.x MultipleWidget: `onChange` passes a Group instance whose
      // `.files()` method returns Array<Promise<fileInfo>> — one promise per
      // selected file that resolves once that file has finished uploading.
      var rawFiles = null;
      if (group && typeof group.files === 'function') {
        try { rawFiles = group.files(); } catch (_) { rawFiles = null; }
      }
      if (!Array.isArray(rawFiles)) return Promise.resolve([]);
      return Promise.all(rawFiles).then(function (files) {
        return files
          .map(function (f) {
            if (!f || !f.cdnUrl) return null;
            return {
              url: f.cdnUrl + (f.name ? f.name : ''),
              name: f.name || '',
              type: 'image',
            };
          })
          .filter(Boolean);
      });
    }

    function bindWidget() {
      if (typeof window.uploadcare === 'undefined') return false;
      try {
        var widget = multiple ? window.uploadcare.MultipleWidget(input) : window.uploadcare.SingleWidget(input);
        if (multiple) {
          widget.onChange(function (group) {
            if (!group) {
              ctx.attachments = [];
              ctx.uploading = false;
              syncSubmitState(ctx);
              return;
            }
            ctx.uploading = true;
            syncSubmitState(ctx);
            resolveGroupFiles(group)
              .then(function (atts) {
                ctx.attachments = atts;
                ctx.uploading = false;
                if (window.console) console.info('[quote-form] uploadcare attachments ready:', atts);
                if (ctx.renderAttachmentsList) ctx.renderAttachmentsList();
                syncSubmitState(ctx);
              })
              .catch(function (err) {
                if (window.console) console.warn('[quote-form] uploadcare file resolution failed', err);
                ctx.attachments = [];
                ctx.uploading = false;
                syncSubmitState(ctx);
              });
          });
        } else {
          widget.onUploadComplete(function (info) {
            if (!info || !info.cdnUrl) return;
            ctx.attachments = [{
              url: info.cdnUrl + (info.name ? info.name : ''),
              name: info.name || '',
              type: 'image',
            }];
            ctx.uploading = false;
            if (ctx.renderAttachmentsList) ctx.renderAttachmentsList();
            syncSubmitState(ctx);
          });
          widget.onChange(function (info) {
            if (!info) {
              ctx.attachments = [];
              ctx.uploading = false;
              syncSubmitState(ctx);
            } else {
              ctx.uploading = true;
              syncSubmitState(ctx);
            }
          });
        }
      } catch (err) {
        if (window.console) console.warn('[quote-form] uploadcare init failed', err);
      }
      return true;
    }

    // Drag-and-drop into the artwork region. Files dropped here upload via
    // uploadcare.fileFrom('object', ...) and append to ctx.attachments.
    // The Uploadcare widget button is also still functional below.
    function isAllowedExt(name) {
      var ext = String(name || '').split('.').pop().toLowerCase();
      return ALLOWED_EXT.indexOf(ext) !== -1;
    }

    // Pending uploads — keyed by an internal id so we can update the chip
    // while the upload is in-flight (progress %, then final filename).
    ctx.pendingUploads = ctx.pendingUploads || [];
    var nextPendingId = 1;

    function renderAttachmentsList() {
      var list = $(ctx.root, '[data-quote-attachments-list]');
      if (!list) return;
      var hasItems = ctx.attachments.length > 0 || ctx.pendingUploads.length > 0;
      list.hidden = !hasItems;
      list.innerHTML = '';
      ctx.attachments.forEach(function (a) {
        var li = document.createElement('li');
        li.className = 'quote-form__attachment';
        var name = document.createElement('span');
        name.className = 'quote-form__attachment-name';
        name.textContent = a.name || 'Attachment';
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'quote-form__attachment-remove';
        remove.setAttribute('aria-label', 'Remove ' + (a.name || 'attachment'));
        remove.textContent = '×';
        remove.addEventListener('click', function () {
          ctx.attachments = ctx.attachments.filter(function (x) { return x !== a; });
          renderAttachmentsList();
          syncSubmitState(ctx);
        });
        li.appendChild(name);
        li.appendChild(remove);
        list.appendChild(li);
      });
      ctx.pendingUploads.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'quote-form__attachment quote-form__attachment--uploading';
        var name = document.createElement('span');
        name.className = 'quote-form__attachment-name';
        name.textContent = p.name;
        var pct = document.createElement('span');
        pct.className = 'quote-form__attachment-progress';
        pct.textContent = (p.progress != null ? Math.round(p.progress * 100) : 0) + '%';
        li.appendChild(name);
        li.appendChild(pct);
        var bar = document.createElement('div');
        bar.className = 'quote-form__attachment-bar';
        bar.style.width = ((p.progress != null ? p.progress : 0) * 100) + '%';
        li.appendChild(bar);
        list.appendChild(li);
      });
    }

    function uploadOneFile(file) {
      var pending = { id: nextPendingId++, name: file.name || 'file', progress: 0 };
      ctx.pendingUploads.push(pending);
      renderAttachmentsList();

      return new Promise(function (resolve) {
        try {
          var uc = window.uploadcare.fileFrom('object', file);
          if (uc && typeof uc.progress === 'function') {
            uc.progress(function (info) {
              if (info && typeof info.progress === 'number') {
                pending.progress = info.progress;
                renderAttachmentsList();
              } else if (info && typeof info.uploadProgress === 'number') {
                pending.progress = info.uploadProgress;
                renderAttachmentsList();
              }
            });
          }
          uc.done(function (info) {
            ctx.pendingUploads = ctx.pendingUploads.filter(function (p) { return p.id !== pending.id; });
            resolve({
              url: (info && info.cdnUrl ? info.cdnUrl : '') + ((info && info.name) ? info.name : ''),
              name: (info && info.name) || file.name || '',
              type: 'image',
            });
          });
          uc.fail(function (err) {
            if (window.console) console.warn('[quote-form] uploadcare drop upload failed', err, file.name);
            ctx.pendingUploads = ctx.pendingUploads.filter(function (p) { return p.id !== pending.id; });
            renderAttachmentsList();
            resolve(null);
          });
        } catch (err) {
          if (window.console) console.warn('[quote-form] uploadcare.fileFrom threw', err);
          ctx.pendingUploads = ctx.pendingUploads.filter(function (p) { return p.id !== pending.id; });
          renderAttachmentsList();
          resolve(null);
        }
      });
    }

    function whenUploadcareReady(cb) {
      if (window.uploadcare && typeof window.uploadcare.fileFrom === 'function') {
        cb();
        return;
      }
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        if (window.uploadcare && typeof window.uploadcare.fileFrom === 'function') {
          clearInterval(iv);
          cb();
        } else if (attempts > 100) {
          clearInterval(iv);
          if (window.console) console.warn('[quote-form] uploadcare never loaded — drag/drop disabled');
        }
      }, 100);
    }

    function handleDroppedFiles(files) {
      var arr = Array.prototype.slice.call(files || []);
      if (window.console) console.info('[quote-form] drop received', arr.length, 'file(s)');
      var accepted = arr.filter(function (f) {
        if (!isAllowedExt(f.name)) {
          if (window.console) console.warn('[quote-form] dropped file rejected (extension):', f.name);
          return false;
        }
        if (f.size > MAX_FILE_BYTES) {
          if (window.console) console.warn('[quote-form] dropped file rejected (>10MB):', f.name);
          return false;
        }
        return true;
      }).slice(0, MAX_FILES - ctx.attachments.length);
      if (!accepted.length) return;

      whenUploadcareReady(function () {
        ctx.uploading = true;
        syncSubmitState(ctx);
        Promise.all(accepted.map(uploadOneFile)).then(function (results) {
          var added = results.filter(Boolean);
          ctx.attachments = ctx.attachments.concat(added).slice(0, MAX_FILES);
          ctx.uploading = ctx.pendingUploads.length > 0;
          if (window.console) console.info('[quote-form] drop attachments resolved:', added);
          renderAttachmentsList();
          syncSubmitState(ctx);
        });
      });
    }

    // preventDefault on every drag event in the chain — without it, the
    // browser navigates to the dropped file's URL on `drop`.
    ['dragenter', 'dragover'].forEach(function (evtName) {
      artworkRegion.addEventListener(evtName, function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        artworkRegion.classList.add('quote-form__artwork--dragover');
        if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
      });
    });
    artworkRegion.addEventListener('dragleave', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      // Only un-highlight when we're leaving the region itself, not a child.
      var rect = artworkRegion.getBoundingClientRect();
      if (ev.clientX < rect.left || ev.clientX >= rect.right ||
          ev.clientY < rect.top || ev.clientY >= rect.bottom) {
        artworkRegion.classList.remove('quote-form__artwork--dragover');
      }
    });
    artworkRegion.addEventListener('drop', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      artworkRegion.classList.remove('quote-form__artwork--dragover');
      handleDroppedFiles(ev.dataTransfer && ev.dataTransfer.files);
    });

    // Make the attachments-list element visible to renderAttachmentsList()
    // when the widget upload path runs too.
    ctx.renderAttachmentsList = renderAttachmentsList;

    if (!bindWidget()) {
      var attempts = 0;
      var iv = setInterval(function () {
        attempts += 1;
        if (bindWidget() || attempts > 50) clearInterval(iv);
      }, 100);
    }
  }

  // -- pricing computation --------------------------------------------------

  // Smallest quantity break in a single pricing row ({qty: price}), or null.
  function minQtyOfRow(row) {
    if (!row) return null;
    var ks = Object.keys(row).map(function (k) { return parseInt(k, 10); }).filter(isFinite);
    return ks.length ? Math.min.apply(null, ks) : null;
  }

  // Smallest quantity break across every row in the pricing table — the
  // product-wide MOQ, used as a fallback before a pricing row is active.
  function minQtyOfTable(ctx) {
    var table = (ctx.config.pricing_table && ctx.config.pricing_table.pricing_table_json) || {};
    var mins = [];
    Object.keys(table).forEach(function (k) {
      var m = minQtyOfRow(table[k]);
      if (m != null) mins.push(m);
    });
    return mins.length ? Math.min.apply(null, mins) : null;
  }

  function activePricingRow(ctx) {
    var product = ctx.config.product || {};
    var table = (ctx.config.pricing_table && ctx.config.pricing_table.pricing_table_json) || {};
    if (product.pricing_attribute && product.pricing_attribute > 0) {
      var pricingAttr = getPricingAttribute(ctx.config);
      if (pricingAttr) {
        var sel = ctx.root.querySelector('select[data-attribute-id="' + pricingAttr.attribute_id + '"]');
        var optId = sel && sel.value ? sel.value : null;
        if (optId && table[optId]) return table[optId];
      }
    }
    return table.qty_only || null;
  }

  function nearestQtyBreak(qty, breaks) {
    if (!breaks.length) return null;
    var sorted = breaks.slice().sort(function (a, b) { return a - b; });
    var picked = sorted[0];
    for (var i = 0; i < sorted.length; i++) {
      if (sorted[i] <= qty) picked = sorted[i];
      else break;
    }
    return picked;
  }

  function computeTotals(ctx) {
    var qty = parseInt(($(ctx.root, '[data-quote-quantity]') || {}).value, 10);
    if (!isFinite(qty) || qty <= 0) qty = 0;

    var row = activePricingRow(ctx);
    var breaks = row ? Object.keys(row).map(function (k) { return parseInt(k, 10); }) : [];
    var nearest = nearestQtyBreak(qty || (breaks.length ? Math.min.apply(null, breaks) : 0), breaks);
    var baseUnit = row && nearest != null ? Number(row[String(nearest)]) || 0 : 0;

    var perUnit = baseUnit;
    var flat = 0;

    var pricingId = (ctx.config.product && ctx.config.product.pricing_attribute) || 0;

    // Attribute selections
    $$(ctx.root, 'select[data-attribute-id]').forEach(function (sel) {
      var attrId = parseInt(sel.dataset.attributeId, 10);
      if (attrId === pricingId) return; // base unit price already includes the size tier
      var opt = sel.selectedOptions && sel.selectedOptions[0];
      if (!opt || !opt.value) return;
      var price = Number(opt.dataset.price) || 0;
      if (sel.dataset.perOrder === '1') flat += price;
      else perUnit += price;
    });

    $$(ctx.root, 'input[type="checkbox"][data-attribute-id]').forEach(function (cb) {
      if (!cb.checked) return;
      var price = Number(cb.dataset.price) || 0;
      if (cb.dataset.perOrder === '1') flat += price;
      else perUnit += price;
    });

    var total = perUnit * qty + flat;
    return { qty: qty, perUnit: perUnit, flat: flat, total: total };
  }

  function refreshEstimate(ctx) {
    var t = computeTotals(ctx);
    var el = $(ctx.root, '[data-quote-estimate]');
    if (!el) return;
    if (t.total > 0) {
      el.textContent = formatAsDollars(t.total);
      el.classList.remove('quote-form__estimate-value--placeholder');
    } else {
      el.textContent = 'Configure above';
      el.classList.add('quote-form__estimate-value--placeholder');
    }
    el.dataset.total = String(t.total);
    el.dataset.perUnit = String(t.perUnit);
    el.dataset.flat = String(t.flat);
    ctx.lastTotals = t;

    // Surface the per-unit × qty (+ flat) math so the customer can
    // sanity-check how we got to the total.
    var bk = $(ctx.root, '[data-quote-estimate-breakdown]');
    if (bk) {
      if (t.total > 0 && t.qty > 0) {
        var parts = [formatAsDollars(t.perUnit) + ' × ' + t.qty];
        if (t.flat > 0) parts.push('+ ' + formatAsDollars(t.flat) + ' flat');
        bk.textContent = parts.join(' ');
        bk.hidden = false;
      } else {
        bk.hidden = true;
        bk.textContent = '';
      }
    }
    // Size-keyed products have a different MOQ per size; keep the note,
    // placeholder, and the input's `min` attribute in sync with the active row.
    if (typeof ctx.refreshMoqNote === 'function') ctx.refreshMoqNote();
    if (typeof ctx.refreshMoqPlaceholder === 'function') ctx.refreshMoqPlaceholder();
    var qty = $(ctx.root, '[data-quote-quantity]');
    var row = activePricingRow(ctx);
    if (qty && row) {
      var ks = Object.keys(row).map(function (k) { return parseInt(k, 10); }).filter(isFinite).sort(function (a, b) { return a - b; });
      if (ks.length) {
        qty.min = String(ks[0]);
      }
    }
  }

  // -- validation -----------------------------------------------------------

  function clearFieldError(field) {
    if (!field) return;
    field.classList.remove('quote-form__field--error');
    var existing = field.querySelector('.quote-form__field-error');
    if (existing) existing.remove();
  }

  function setFieldError(field, message) {
    if (!field) return;
    field.classList.add('quote-form__field--error');
    var existing = field.querySelector('.quote-form__field-error');
    if (existing) existing.remove();
    var p = document.createElement('p');
    p.className = 'quote-form__field-error';
    // role=alert + aria-live=assertive so screen readers announce the
    // validation message immediately when it appears.
    p.setAttribute('role', 'alert');
    p.setAttribute('aria-live', 'assertive');
    p.textContent = message;
    field.appendChild(p);
  }

  function fieldOf(input) {
    return input ? input.closest('.quote-form__field') : null;
  }

  function clearAllErrors(ctx) {
    $$(ctx.root, '.quote-form__field--error').forEach(clearFieldError);
    var err = $(ctx.root, '[data-quote-error]');
    if (err) {
      err.hidden = true;
      err.textContent = '';
    }
  }

  function validateAll(ctx) {
    clearAllErrors(ctx);
    var ok = true;

    var nameInput = $(ctx.root, '[data-quote-name]');
    if (!nameInput.value.trim()) {
      setFieldError(fieldOf(nameInput), 'Please enter your name.');
      ok = false;
    }

    var emailInput = $(ctx.root, '[data-quote-email]');
    if (!EMAIL_RE.test(emailInput.value.trim())) {
      setFieldError(fieldOf(emailInput), 'Enter a valid email address.');
      ok = false;
    }

    var phoneInput = $(ctx.root, '[data-quote-phone]');
    var minLen = parseInt(ctx.phoneMinLength, 10) || 7;
    var phoneDigits = phoneInput.value.replace(/\D/g, '');
    if (phoneDigits.length < minLen) {
      setFieldError(fieldOf(phoneInput), 'Enter at least ' + minLen + ' digits.');
      ok = false;
    }

    var qtyInput = $(ctx.root, '[data-quote-quantity]');
    var qty = parseInt(qtyInput.value, 10);
    var min = parseInt(qtyInput.min, 10);
    if (!isFinite(qty) || (isFinite(min) && qty < min)) {
      setFieldError(fieldOf(qtyInput), 'Enter a numeric quantity of at least ' + (isFinite(min) ? min : 1) + '.');
      ok = false;
    }

    // Required attributes
    var pricingId = (ctx.config.product && ctx.config.product.pricing_attribute) || 0;
    (ctx.config.attributes || []).forEach(function (attr) {
      if (!attr.attribute_is_required) return;
      if (attr.attribute_input_type === 'checkbox') return; // checkboxes are inherently optional
      var sel = ctx.root.querySelector('select[data-attribute-id="' + attr.attribute_id + '"]');
      if (!sel || !sel.value) {
        setFieldError(fieldOf(sel), 'Please make a selection.');
        ok = false;
      }
    });

    if (!ok) {
      // Move focus to the first invalid input so screen reader + keyboard
      // users land on the problem rather than the (still-disabled) submit.
      var firstErr = ctx.root.querySelector('.quote-form__field--error input, .quote-form__field--error select, .quote-form__field--error textarea');
      if (firstErr && typeof firstErr.focus === 'function') {
        try {
          firstErr.focus({ preventScroll: false });
        } catch (_) {
          firstErr.focus();
        }
      }
    }

    return ok;
  }

  // -- submission -----------------------------------------------------------

  function buildSpec(ctx) {
    var spec = {};
    $$(ctx.root, 'select[data-attribute-id]').forEach(function (sel) {
      if (!sel.value) return;
      var opt = sel.selectedOptions && sel.selectedOptions[0];
      if (!opt) return;
      var attrId = parseInt(sel.dataset.attributeId, 10);
      var attr = (ctx.config.attributes || []).find(function (a) { return a.attribute_id === attrId; });
      var key = (attr && attr.attribute_label) || sel.name;
      spec[key] = opt.dataset.label || opt.textContent;
    });
    $$(ctx.root, 'input[type="checkbox"][data-attribute-id]').forEach(function (cb) {
      if (!cb.checked) return;
      var attrId = parseInt(cb.dataset.attributeId, 10);
      var attr = (ctx.config.attributes || []).find(function (a) { return a.attribute_id === attrId; });
      var key = (attr && attr.attribute_label) || cb.name;
      spec[key] = cb.dataset.label || 'Yes';
    });
    return spec;
  }

  function buildNotes(ctx) {
    var lines = [];
    var comments = ($(ctx.root, '[data-quote-comments]') || {}).value || '';
    if (comments) lines.push(comments, '');
    var company = ($(ctx.root, '[data-quote-company]') || {}).value || '';
    lines.push('Company: ' + company);
    // Date Needed and No Rush are mutually exclusive in the UI; mirror that
    // here. Only emit the line that reflects the customer's actual choice.
    var date = ($(ctx.root, '[data-quote-date]') || {}).value || '';
    var noRushInput = $(ctx.root, '[data-quote-no-rush]');
    var noRush = noRushInput && noRushInput.checked;
    if (noRush) {
      lines.push('No Rush: yes');
    } else if (date) {
      lines.push('Date Needed: ' + date);
    }
    // Magic Make ignores `products[].spec` server-side, so mirror the
    // attribute selections into notes where the team will actually see them.
    var spec = buildSpec(ctx);
    var specKeys = Object.keys(spec);
    if (specKeys.length) {
      lines.push('', 'Specifications:');
      specKeys.forEach(function (k) { lines.push('- ' + k + ': ' + spec[k]); });
    }
    // Magic Make permits only `artwork` + `artworkBack` slots (one URL each)
    // and drops `attachments[]`. Stash any additional file URLs in notes so
    // the team gets the full set when a customer uploads multiple files.
    var extras = (ctx.attachments || []).slice(2);
    if (extras.length) {
      lines.push('', 'Additional artwork files:');
      extras.forEach(function (a) { lines.push(a.url + (a.name ? ' (' + a.name + ')' : '')); });
    }
    return lines.join('\n');
  }

  function toArtworkRef(att) {
    if (!att || !att.url) return '';
    return { url: att.url, name: att.name || '', type: 'image' };
  }

  function buildPayload(ctx) {
    var totals = ctx.lastTotals || computeTotals(ctx);
    var atts = ctx.attachments || [];
    // Date and No Rush are mutually exclusive — if the customer chose
    // "No Rush", clear eventDate so Magic Make doesn't see a stale value.
    var noRushInput = $(ctx.root, '[data-quote-no-rush]');
    var noRush = noRushInput && noRushInput.checked;
    var eventDate = noRush ? '' : (($(ctx.root, '[data-quote-date]') || {}).value || '');

    return {
      name: ($(ctx.root, '[data-quote-name]') || {}).value || '',
      email: ($(ctx.root, '[data-quote-email]') || {}).value || '',
      phone: ($(ctx.root, '[data-quote-phone]') || {}).value || '',
      products: [
        {
          spec: buildSpec(ctx),
          title: ctx.product.title,
          handle: ctx.product.handle,
          type: ctx.product.type,
          quantity: totals.qty,
          unitPrice: formatAsDollars(totals.perUnit),
          subTotal: formatAsDollars(totals.perUnit * totals.qty),
          productId: 'gid://shopify/Product/' + ctx.product.id,
          artwork: toArtworkRef(atts[0]),
          artworkBack: toArtworkRef(atts[1]),
          parentItemId: '',
          relatedItemId: '',
        },
      ],
      notes: buildNotes(ctx),
      cart_total: formatAsDollars(totals.total),
      eventDate: eventDate,
      taxDocuments: { url: '', name: '', type: '' },
      mm_send_email_receipt: true,
    };
  }

  function showError(ctx, message) {
    var err = $(ctx.root, '[data-quote-error]');
    if (!err) return;
    err.hidden = false;
    err.textContent = message;
  }

  function syncSubmitState(ctx) {
    var btn = $(ctx.root, '[data-quote-submit]');
    if (!btn) return;
    if (ctx.submitting) {
      btn.disabled = true;
      return;
    }
    if (ctx.uploading) {
      btn.disabled = true;
      btn.textContent = 'Uploading artwork…';
      return;
    }
    btn.textContent = ctx.submitButtonLabel;
    if (ctx.turnstileGated && ctx.root.dataset.turnstileVerified !== 'true') {
      btn.disabled = true;
      return;
    }
    btn.disabled = false;
  }

  function resetTurnstile(ctx) {
    if (!ctx.turnstileGated) return;
    ctx.root.dataset.turnstileVerified = '';
    var widget = $(ctx.root, '.cf-turnstile');
    if (widget && window.turnstile && typeof window.turnstile.reset === 'function') {
      try { window.turnstile.reset(widget); } catch (_) {}
    }
  }

  function showSuccess(ctx) {
    var form = $(ctx.root, '[data-quote-form-element]');
    var success = $(ctx.root, '[data-quote-success]');
    if (form) form.hidden = true;
    if (success) success.hidden = false;
    if (ctx.root.scrollIntoView) ctx.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function clearForm(ctx) {
    $$(ctx.root, 'input, select, textarea').forEach(function (el) {
      if (el.type === 'checkbox') el.checked = false;
      else if (el.tagName === 'SELECT') el.selectedIndex = 0;
      else el.value = '';
    });
    ctx.attachments = [];
    refreshEstimate(ctx);
  }

  function handleSubmit(ctx, ev) {
    ev.preventDefault();
    if (ctx.submitting || ctx.uploading) return;
    if (!validateAll(ctx)) return;

    ctx.submitting = true;
    syncSubmitState(ctx);

    var payload = buildPayload(ctx);
    var url = 'https://forgemachine.io/storefronts/' + encodeURIComponent(ctx.storefrontId) + '/quote_requests';

    // Optimistic UX: Magic Make's response is opaque (mode: no-cors), so we
    // can't read success/failure anyway. Flip to the success view immediately
    // and let the fetch finish in the background. `keepalive: true` lets the
    // request complete even if the user navigates away mid-submit.
    showSuccess(ctx);
    clearForm(ctx);
    resetTurnstile(ctx);
    ctx.submitting = false;
    syncSubmitState(ctx);

    fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(function (err) {
      if (window.console) console.warn('[quote-form] background submit failed', err);
    });
  }

  function bindForm(ctx) {
    var form = $(ctx.root, '[data-quote-form-element]');
    form.addEventListener('submit', function (ev) { handleSubmit(ctx, ev); });

    ctx.root.addEventListener('change', function (ev) {
      var t = ev.target;
      if (t && (t.matches('input, select, textarea'))) {
        clearFieldError(fieldOf(t));
        refreshEstimate(ctx);
      }
    });
    ctx.root.addEventListener('input', function (ev) {
      var t = ev.target;
      if (t && (t.matches('input[type="number"], input[type="email"], input[type="tel"], input[type="text"]'))) {
        clearFieldError(fieldOf(t));
        if (t.matches('[data-quote-quantity]')) refreshEstimate(ctx);
      }
    });
  }

  // -- modal ---------------------------------------------------------------

  var _modal = null;
  var _modalLastFocus = null;

  function getFocusable(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapTab(wrap, ev) {
    if (ev.key !== 'Tab' || wrap.hidden) return;
    var panel = wrap.querySelector('.quote-form-modal__panel');
    if (!panel) return;
    var focusable = getFocusable(panel);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  function ensureModal() {
    if (_modal) return _modal;
    var wrap = document.createElement('div');
    wrap.className = 'quote-form-modal';
    wrap.hidden = true;
    var backdrop = document.createElement('div');
    backdrop.className = 'quote-form-modal__backdrop';
    backdrop.setAttribute('data-modal-close', '');
    var panel = document.createElement('div');
    panel.className = 'quote-form-modal__panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('tabindex', '-1');
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'quote-form-modal__close';
    close.setAttribute('data-modal-close', '');
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    var body = document.createElement('div');
    body.className = 'quote-form-modal__body';
    body.setAttribute('data-modal-body', '');
    panel.appendChild(close);
    panel.appendChild(body);
    wrap.appendChild(backdrop);
    wrap.appendChild(panel);
    document.body.appendChild(wrap);
    wrap.addEventListener('click', function (ev) {
      if (ev.target.closest('[data-modal-close]')) closeModal();
    });
    document.addEventListener('keydown', function (ev) {
      if (wrap.hidden) return;
      if (ev.key === 'Escape') closeModal();
      else if (ev.key === 'Tab') trapTab(wrap, ev);
    });
    _modal = wrap;
    return wrap;
  }

  function openModal(content) {
    var wrap = ensureModal();
    var body = wrap.querySelector('[data-modal-body]');
    body.innerHTML = '';
    body.appendChild(content);
    var panel = wrap.querySelector('.quote-form-modal__panel');

    // Wire aria-labelledby: give the modal's own h3 a stable id so screen
    // readers announce the title when the dialog opens.
    var title = body.querySelector('.quote-form-modal__title');
    if (title) {
      if (!title.id) title.id = 'quote-form-modal-title';
      panel.setAttribute('aria-labelledby', title.id);
    } else {
      panel.removeAttribute('aria-labelledby');
    }

    _modalLastFocus = document.activeElement;
    wrap.hidden = false;
    document.body.style.overflow = 'hidden';

    // Defer focus to the close button so SR users hear the title + "Close"
    // rather than landing on the dialog container.
    var closeBtn = wrap.querySelector('.quote-form-modal__close');
    if (closeBtn) {
      try { closeBtn.focus(); } catch (_) {}
    }
  }

  function closeModal() {
    if (!_modal) return;
    _modal.hidden = true;
    document.body.style.overflow = '';
    if (_modalLastFocus && typeof _modalLastFocus.focus === 'function') {
      try { _modalLastFocus.focus(); } catch (_) {}
    }
    _modalLastFocus = null;
  }

  function buildOptionsModalContent(attr) {
    var fragment = document.createDocumentFragment();
    var heading = document.createElement('h3');
    heading.className = 'quote-form-modal__title';
    heading.textContent = attr.attribute_label + ' Options';
    fragment.appendChild(heading);

    var grid = document.createElement('div');
    grid.className = 'quote-form-modal__grid';
    (attr.attribute_options || []).forEach(function (opt) {
      var card = document.createElement('div');
      card.className = 'quote-form-modal__option';
      var src = pickImage(opt.detailed_image) || pickImage(opt.dropdown_image);
      if (src) {
        var img = document.createElement('img');
        img.src = src;
        img.alt = opt.label || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 120;
        img.height = 120;
        card.appendChild(img);
      }
      var lbl = document.createElement('div');
      lbl.className = 'quote-form-modal__option-label';
      lbl.textContent = opt.display_label || opt.label;
      card.appendChild(lbl);
      grid.appendChild(card);
    });
    fragment.appendChild(grid);
    return fragment;
  }

  function buildPricingModalContent(ctx) {
    var fragment = document.createDocumentFragment();
    var heading = document.createElement('h3');
    heading.className = 'quote-form-modal__title';
    heading.textContent = 'Pricing Table';
    fragment.appendChild(heading);

    var product = ctx.config.product || {};
    var table = (ctx.config.pricing_table && ctx.config.pricing_table.pricing_table_json) || {};
    var pricingAttr = product.pricing_attribute ? getPricingAttribute(ctx.config) : null;

    var rows = []; // { label, prices: { qty: price } }
    var qtyKeys = {};
    if (pricingAttr && product.pricing_attribute && Object.keys(table).length && !table.qty_only) {
      var resolved = applyOverrides(pricingAttr);
      (resolved.attribute_options || []).forEach(function (opt) {
        var row = table[String(opt.id)];
        if (!row) return;
        rows.push({ label: opt.display_label || opt.label, prices: row });
        Object.keys(row).forEach(function (k) { qtyKeys[k] = true; });
      });
    } else if (table.qty_only) {
      rows.push({ label: 'Price Each', prices: table.qty_only });
      Object.keys(table.qty_only).forEach(function (k) { qtyKeys[k] = true; });
    }

    var qtys = Object.keys(qtyKeys).map(function (k) { return parseInt(k, 10); }).sort(function (a, b) { return a - b; });

    if (!rows.length || !qtys.length) {
      var msg = document.createElement('p');
      msg.textContent = 'Pricing not available for this product.';
      msg.style.textAlign = 'center';
      fragment.appendChild(msg);
      return fragment;
    }

    var tbl = document.createElement('table');
    tbl.className = 'quote-form-modal__table';
    var thead = document.createElement('thead');
    var hr = document.createElement('tr');
    var firstTh = document.createElement('th');
    firstTh.textContent = 'Quantity';
    hr.appendChild(firstTh);
    qtys.forEach(function (q) {
      var th = document.createElement('th');
      th.textContent = String(q);
      hr.appendChild(th);
    });
    thead.appendChild(hr);
    tbl.appendChild(thead);

    var tbody = document.createElement('tbody');
    rows.forEach(function (r) {
      var tr = document.createElement('tr');
      var lblTd = document.createElement('td');
      lblTd.textContent = r.label;
      lblTd.style.fontWeight = '600';
      tr.appendChild(lblTd);
      qtys.forEach(function (q) {
        var td = document.createElement('td');
        var price = r.prices[String(q)];
        td.textContent = price != null ? formatAsDollars(Number(price)) : '—';
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
    });
    tbl.appendChild(tbody);
    fragment.appendChild(tbl);
    return fragment;
  }

  // -- bootstrap -----------------------------------------------------------

  function init(root) {
    var config = parseConfig(root);
    if (!config || !config.product) {
      // Liquid no longer gates on the metafield; defend client-side so
      // an un-bootstrapped product doesn't expose a half-rendered form
      // that submits to nowhere. Bootstrap via scripts/export-quote-config.mjs
      // to light up the full quote experience.
      if (window.console) {
        console.warn('[quote-form] lapelpins.quote_config metafield missing on this product — hiding form. Run scripts/export-quote-config.mjs --product-id <id> to enable.');
      }
      root.hidden = true;
      return;
    }

    var submitBtn = root.querySelector('[data-quote-submit]');
    var ctx = {
      root: root,
      sectionId: root.dataset.sectionId || 'quote',
      config: config,
      product: {
        id: root.dataset.productId,
        handle: root.dataset.productHandle,
        title: root.dataset.productTitle,
        type: root.dataset.productType,
      },
      storefrontId: root.dataset.storefrontId,
      uploadcareKey: root.dataset.uploadcareKey,
      phoneMinLength: root.dataset.phoneMinLength || '7',
      attachments: [],
      submitting: false,
      uploading: false,
      submitButtonLabel: submitBtn ? submitBtn.textContent : 'Get Quote Now',
      turnstileGated: !!root.querySelector('.cf-turnstile'),
    };

    // Expose Uploadcare public key globally for the widget
    if (ctx.uploadcareKey && !window.UPLOADCARE_PUBLIC_KEY) {
      window.UPLOADCARE_PUBLIC_KEY = ctx.uploadcareKey;
    }

    renderHeading(ctx);
    renderPrimary(ctx);
    renderAttributes(ctx);
    setupUploader(ctx);
    bindForm(ctx);
    bindPhoneFormatting(ctx);
    refreshEstimate(ctx);
    armTurnstileFallback(ctx);
  }

  // If Turnstile gates the button but never resolves within
  // TURNSTILE_FALLBACK_MS, fail open: enable the button so the customer can
  // still submit. A real pass (or the error callback) flips
  // turnstileVerified to 'true' first, in which case this is a no-op.
  function armTurnstileFallback(ctx) {
    if (!ctx.turnstileGated) return;
    setTimeout(function () {
      if (ctx.root.dataset.turnstileVerified === 'true') return;
      ctx.root.dataset.turnstileVerified = 'true';
      ctx.root.dataset.turnstileFallback = '1';
      var status = $(ctx.root, '[data-quote-submit-status]');
      if (status) status.textContent = '';
      syncSubmitState(ctx);
      if (window.console) {
        console.info('[quote-form] Turnstile did not resolve in ' + TURNSTILE_FALLBACK_MS + 'ms — enabling submit (fail-open).');
      }
    }, TURNSTILE_FALLBACK_MS);
  }

  function bindPhoneFormatting(ctx) {
    var phone = $(ctx.root, '[data-quote-phone]');
    if (!phone) return;
    phone.setAttribute('autocomplete', 'tel');
    phone.setAttribute('placeholder', '(555) 123-4567');
    phone.setAttribute('inputmode', 'tel');
    phone.addEventListener('input', function () {
      var caretAtEnd = phone.selectionStart === phone.value.length;
      phone.value = formatPhone(phone.value);
      if (caretAtEnd) {
        var len = phone.value.length;
        try { phone.setSelectionRange(len, len); } catch (_) {}
      }
    });
    phone.addEventListener('blur', function () {
      phone.value = formatPhone(phone.value);
    });
  }

  function start() {
    var roots = document.querySelectorAll('[data-quote-form]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
